import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export type TopShopStatus = "submitted" | "accepted" | "waitlisted" | "declined" | "withdrawn" | "completed";

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  submitted: { label: "Submitted", className: "bg-[#1C61E0] text-white" },
  accepted: { label: "Accepted", className: "bg-[#22891D] text-white" },
  waitlisted: { label: "Waitlisted", className: "bg-[#F39001] text-white" },
  declined: { label: "Declined", className: "bg-muted text-muted-foreground" },
  withdrawn: { label: "Withdrawn", className: "bg-muted text-muted-foreground" },
  completed: { label: "Completed", className: "bg-[#14532D] text-white" },
};

export function TopShopStatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.submitted;
  return (
    <span className={cn("inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold", s.className)}>
      {s.label}
    </span>
  );
}

export const IMPACT_LABELS: Record<string, string> = {
  none: "No damage",
  minor: "Minor damage",
  moderate: "Moderate damage",
  severe: "Severe damage",
  destroyed: "Destroyed",
};

export const MAX_PHOTOS = 5;

/** Loads the signed-in user's top-shop application with joined names and photos. */
export function useMyTopShopApplication() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["top-shop", "my-application", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data: program, error: pErr } = await supabase
        .from("programs").select("id").eq("slug", "top-shop").maybeSingle();
      if (pErr) throw pErr;
      if (!program) return null;

      const { data: app, error } = await supabase
        .from("program_applications")
        .select("*, business_type:program_business_types(name), community:program_communities(name, parish)")
        .eq("program_id", program.id)
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!app) return null;

      const [{ data: areas, error: aErr }, { data: photos, error: phErr }] = await Promise.all([
        supabase.from("program_focus_areas").select("id, name, sort_order").eq("program_id", program.id),
        supabase.from("program_application_photos").select("*").eq("application_id", app.id)
          .order("sort_order", { ascending: true }).order("created_at", { ascending: true }),
      ]);
      if (aErr) throw aErr;
      if (phErr) throw phErr;

      const areaMap = new Map((areas ?? []).map((a) => [a.id, a.name]));
      const focusAreaNames = (app.focus_area_ids ?? []).map((id: string) => areaMap.get(id)).filter(Boolean) as string[];

      return { application: app as any, focusAreaNames, photos: photos ?? [] };
    },
  });
}

/** Resize to max long edge and compress to JPEG. */
export function resizeToJpeg(file: File, maxEdge = 1600, quality = 0.82): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Could not process image"));
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not process image"))), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}
